import type { FunctionComponent } from "react";
import { useParams } from "react-router-dom";
import Spinner from "../../../../components/spinner.components";
import { IPayoutType } from "../../../../types/models/payout-transaction.modals.types";
import { DateTime } from "luxon";
import { Separator } from "../../../../components/ui/separator";
import useWalletTranasactionsStore from "../../../../store/wallet-transactions.store";
import { SheetTitle } from "../../../../components/ui/sheet";
import { Button } from "../../../../components/ui/button";
import { ExternalLink, FileText } from "lucide-react";

export const WalletTransactionDetailsSliderAdminPartial: FunctionComponent =
	() => {
		const { transactionId } = useParams<{ transactionId: string }>();
		const { transactions } = useWalletTranasactionsStore();

		const transaction = transactions?.find((t) => t.id === transactionId);

		if (!transaction) {
			return (
				<div className="flex justify-center items-center w-full h-full">
					<Spinner width={30} />
				</div>
			);
		}

		const isCredit = transaction.transactionType === IPayoutType.CREDITED;

		return (
			<div className="mx-auto rounded-xl w-full max-w-3xl overflow-y-auto h-full pb-10">
				<SheetTitle className="p-5 border-border border-b font-semibold text-white text-xl">
					{transaction.creator.name}
				</SheetTitle>

				{/* Basic Info */}
				<div className="gap-x-12 gap-y-10 grid grid-cols-2 p-5">
					<div>
						<div className="mb-1 text-gray-400 text-sm">Creator Phone</div>
						<div className="font-semibold text-white text-xl">
							{transaction.creator.phoneNumber || "—"}
						</div>
					</div>
					<div>
						<div className="mb-1 text-gray-400 text-sm">Created at</div>
						<div className="font-semibold text-white text-xl">
							{transaction.createdAt
								? DateTime.fromISO(transaction.createdAt.toString()).toFormat(
										"dd-MM-yyyy",
									)
								: "—"}
						</div>
					</div>
				</div>

				<Separator />

				{/* Amount Details */}
				<div className="gap-x-12 gap-y-10 grid grid-cols-2 p-5">
					<div>
						<div className="mb-1 text-gray-400 text-sm">Total Amount</div>
						<div
							className={`font-semibold text-xl ${isCredit ? "text-green-500" : "text-red-500"}`}
						>
							{isCredit ? "+" : "-"}₹{transaction.totalAmount?.toLocaleString()}
						</div>
					</div>
					<div>
						<div className="mb-1 text-gray-400 text-sm">Amount Details</div>
						<div className="flex flex-col gap-1">
							<div className="font-semibold text-white text-xl">
								₹{transaction.amount?.toLocaleString() || "—"}
							</div>
							{(transaction.tds !== null && transaction.tds !== undefined) && (
								<p className="text-secondary text-xs">
									TDS: -₹{transaction.tds.toLocaleString()}
								</p>
							)}
						</div>
					</div>
				</div>

				<Separator />

				{/* Transaction Status */}
				<div className="gap-x-12 gap-y-10 grid grid-cols-2 p-5">
					<div>
						<div className="mb-1 text-gray-400 text-sm">Status</div>
						<div className="font-semibold text-white text-xl capitalize">
							{transaction.transactionStatus.status}
						</div>
					</div>
					<div>
						<div className="mb-1 text-gray-400 text-sm">Status Date</div>
						<div className="font-semibold text-white text-xl">
							{DateTime.fromISO(
								transaction.transactionStatus.timestamp.toString(),
							).toLocaleString(DateTime.DATETIME_MED)}
						</div>
					</div>
					{transaction.transactionStatus.message && (
						<div className="col-span-2">
							<div className="mb-1 text-gray-400 text-sm">Status Message</div>
							<div className="font-semibold text-white text-base">
								{transaction.transactionStatus.message}
							</div>
						</div>
					)}
					{transaction.cashfreeTransactionId && (
						<div className="col-span-2">
							<div className="mb-1 text-gray-400 text-sm">Cashfree ID</div>
							<div className="font-semibold text-white text-base break-all">
								{transaction.cashfreeTransactionId}
							</div>
						</div>
					)}
				</div>

				<Separator />

				{/* Payout Method */}
				<div className="p-5">
					<p className="font-semibold text-white text-xl mb-4">Payout Method</p>
					<div className="gap-x-12 gap-y-10 grid grid-cols-2">
						{transaction.bankAccountDetails ? (
							<>
								<div>
									<div className="mb-1 text-gray-400 text-sm">Account Number</div>
									<div className="font-semibold text-white text-xl">
										{transaction.bankAccountDetails.accountNumber}
									</div>
								</div>
								<div>
									<div className="mb-1 text-gray-400 text-sm">IFSC Code</div>
									<div className="font-semibold text-white text-xl">
										{transaction.bankAccountDetails.ifscCode}
									</div>
								</div>
							</>
						) : transaction.upiDetails ? (
							<div>
								<div className="mb-1 text-gray-400 text-sm">UPI ID</div>
								<div className="font-semibold text-white text-xl">
									{transaction.upiDetails.vpaId}
								</div>
							</div>
						) : (
							<div>
								<div className="mb-1 text-gray-400 text-sm">Details</div>
								<div className="font-semibold text-white text-xl">—</div>
							</div>
						)}
					</div>
				</div>

				<Separator />

				{/* Invoice Details */}
				{transaction.invoiceDetails && (
					<>
						<div className="p-5">
							<p className="font-semibold text-white text-xl mb-4">Invoice Details</p>
							<div className="gap-x-12 gap-y-10 grid grid-cols-2">
								<div>
									<div className="mb-1 text-gray-400 text-sm">Invoice Number</div>
									<div className="font-semibold text-white text-xl">
										{transaction.invoiceDetails.invoiceNumber}
									</div>
								</div>
								<div>
									<div className="mb-1 text-gray-400 text-sm">Invoice Date</div>
									<div className="font-semibold text-white text-xl">
										{DateTime.fromISO(
											transaction.invoiceDetails.dateOfInvoice.toString(),
										).toFormat("dd-MM-yyyy")}
									</div>
								</div>
								<div>
									<div className="mb-1 text-gray-400 text-sm">Invoice Amount</div>
									<div className="font-semibold text-white text-xl">
										₹{transaction.invoiceDetails.amount.toLocaleString()}
									</div>
								</div>
							</div>

							{transaction.invoiceDetails.invoiceFileUrl && (
								<div className="mt-8 flex flex-col gap-4">
									<div className="flex items-center justify-between bg-tertiarybuttonbg p-4 rounded-xl border border-border">
										<div className="flex items-center gap-3">
											<div className="bg-primary/10 p-2 rounded-lg">
												<FileText className="text-primary w-6 h-6" />
											</div>
											<div>
												<p className="font-semibold text-white">Invoice File</p>
												<p className="text-xs text-secondary">Click to view or download</p>
											</div>
										</div>
										<Button
											onClick={() => window.open(transaction.invoiceDetails?.invoiceFileUrl || "", "_blank")}
											variant="outline"
											className="flex items-center gap-2"
										>
											View <ExternalLink className="w-4 h-4" />
										</Button>
									</div>
									
									<div className="rounded-xl border border-border overflow-hidden h-[500px]">
										<iframe
											src={transaction.invoiceDetails.invoiceFileUrl}
											className="w-full h-full"
											title="Invoice PDF"
											style={{ border: "none" }}
										/>
									</div>
								</div>
							)}
						</div>
					</>
				)}
			</div>
		);
	};
